import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ChevronDown,
  Mic,
  Volume2,
  VolumeX,
  MessageCircle,
  AudioLines,
  Plus,
  Settings,
} from "lucide-react";
import { cdnUrl, cn } from "@/lib/utils";
import { env } from "@/config/env";
import { useAdsWorkspaceStore } from "@/features/ads";
import { useAgentChatStore } from "../store/useAgentChatStore";
import { AGENT_PERSONAS } from "@/config/agents";
import { resolveAgentId } from "@/config/agentSystemPrompts";
import {
  ADS_SUB_AGENTS,
  COMPANION_AGENT_TAGS,
  getCompanionSubAgent,
  supportsSubAgentAssetStrip,
} from "../lib/chatAgentRegistry";
import type { CompanionTokenUsageSnapshot } from "../lib/companionChatRuntime";
import { useSubAgentStatus } from "./hooks/useSubAgentStatus";
import type { AvatarHandle } from "./agent-chat/types";
import { SimliAvatar } from "./agent-chat/SimliAvatar";
import { SubAgentSessionAssetsStrip } from "./SubAgentSessionAssetsStrip";

export interface AvatarHeroProps {
  avatarRef: React.RefObject<AvatarHandle | null>;
  avatarProvider: string;
  avatarConnected: boolean;
  avatarConnecting: boolean;
  hasValidAvatarId: boolean;
  liveAvatarId?: string;
  simliFaceId?: string;
  voiceId?: string;
  setAvatarConnected: (c: boolean) => void;
  setAvatarConnecting: (c: boolean) => void;
  scheduleAvatarSpeakingStart: () => void;
  scheduleAvatarSpeakingStop: () => void;
  onTextAudioReady?: (text: string, durationMs: number) => void;
}

interface ChatPanelHeaderProps {
  /** Visual density for feature-specific shells. */
  headerVariant?: "default" | "adsCli";
  /** Active session name (Marcus only) — shown as a pill. */
  sessionName?: string | null;
  /** ISO string / epoch for when the active session was created. */
  sessionCreatedAt?: string | number | null;
  /** Whether Marcus is currently streaming a response. */
  isStreaming?: boolean;
  /** True while Anthropic is running a compaction edit (drives the meter's active state). */
  isCompacting?: boolean;
  /** Elapsed ms since the user sent the current turn — kept visible through tool calls. */
  turnElapsedMs?: number | null;
  /** Running input-token count vs the compact threshold — shown as a small ring. */
  tokenUsage?: CompanionTokenUsageSnapshot | null;
  /** Whether the avatar is currently speaking (used for hero glow). */
  avatarSpeaking?: boolean;
  /** Live avatar hero section data (Marcus only). When provided, renders a hero above controls. */
  avatarHero?: AvatarHeroProps;
  /** Open the session drawer (Marcus only). */
  onOpenSessions?: () => void;
  /** Override back navigation for feature-specific shells. */
  onBackToList?: () => void;
  /** Create a fresh Marcus session. */
  onCreateSession?: () => void | Promise<void>;
  /** Disable session actions while the session list is resolving or creating. */
  sessionControlsDisabled?: boolean;
  /** Optional right-side controls supplied by feature-specific shells. */
  headerActions?: React.ReactNode;
  /** Voice/chat mode controls (Marcus only). */
  voiceControls?: {
    isChatMode: boolean;
    isListening: boolean;
    isSecureMicContext: boolean;
    avatarMuted: boolean;
    voiceVolume: number;
    toggleListen: () => void;
    setAvatarMuted: (m: boolean) => void;
    setVoiceVolume: (v: number) => void;
    setCompanionMode: (mode: "voice" | "chat") => void;
  };
}

/**
 * Phase 11-7c — top-of-panel name/tag for the active orchestrator.
 *
 * The header was originally hard-coded to "Marcus" / "Growth" because
 * it was built for the Ads workspace. When the wizard mounts a
 * different unit's companion (e.g. Cole on Forge / Business Ops, Remi
 * on Brand-DNA / Creative), we resolve the persona from the connected
 * workflow slug and use its `humanName` + a unit-aware tag instead.
 */
/** Static headshot shown in the hero while the live Simli avatar connects (Marcus only),
 *  framed to match the Simli render so the handoff is seamless. */
const MARCUS_HERO_POSTER_SRC = cdnUrl("/Marcus-onboarding.jpg");

const WORKFLOW_TAG: Record<string, string> = {
  "growth-copilot": "Growth",
  "content-studio-copilot": "Content",
  "social-studio-copilot": "Social",
  "marcus-video-editor": "Video Editor",
};

function formatAge(ts?: string | number | null): string | null {
  if (ts == null) return null;
  const then = typeof ts === "string" ? Date.parse(ts) : ts;
  if (!Number.isFinite(then)) return null;
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return "now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  const wk = Math.floor(day / 7);
  return `${wk}w`;
}

/** Format the turn timer for the header sub-line. Matches BananJobCard's
 *  "Xs" / "Xm Ys" style so timers across the app read the same. */
function formatHeaderElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

/** Re-render every 30s so the relative age stays fresh while the panel is open. */
function useTick(enabled: boolean, intervalMs = 30_000) {
  const [, setT] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setT((v) => v + 1), intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs]);
}

/**
 * Small circular progress ring showing how full the model's context window
 * is. Matches Claude Code's built-in statusline method: numerator is the
 * total context tokens (input + cache reads + cache writes), denominator is
 * the model's context window size. Colors shift green → amber → red as the
 * window fills, and the ring pulses while a compaction edit is running.
 */
function ContextFillMeter({
  contextTokens,
  contextWindow,
  isCompacting,
}: {
  contextTokens: number;
  contextWindow: number;
  isCompacting?: boolean;
}) {
  const pct = Math.max(0, Math.min(100, Math.round((contextTokens / contextWindow) * 100)));
  const radius = 8;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - pct / 100);
  const ring = isCompacting
    ? "stroke-primary"
    : pct >= 90
      ? "stroke-red-400"
      : pct >= 70
        ? "stroke-amber-400"
        : "stroke-emerald-400";
  const label = `Context: ${contextTokens.toLocaleString()} / ${contextWindow.toLocaleString()} tokens (${pct}% used)`;
  return (
    <div
      className={cn(
        "relative flex items-center justify-center w-6 h-6 shrink-0",
        isCompacting && "animate-pulse",
      )}
      title={label}
      aria-label={label}
    >
      <svg viewBox="0 0 20 20" className="w-5 h-5 -rotate-90">
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          strokeWidth="2"
          className="stroke-border/40"
        />
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          className={cn(ring, "transition-[stroke-dashoffset] duration-500 ease-out")}
        />
      </svg>
      <span className="absolute text-[8px] font-semibold leading-none text-foreground/70 tabular-nums">
        {pct}
      </span>
    </div>
  );
}

export function ChatPanelHeader({
  headerVariant = "default",
  sessionName,
  sessionCreatedAt,
  isStreaming,
  isCompacting,
  turnElapsedMs,
  tokenUsage,
  avatarSpeaking = false,
  avatarHero,
  onOpenSessions,
  onBackToList,
  onCreateSession,
  sessionControlsDisabled,
  headerActions,
  voiceControls,
}: ChatPanelHeaderProps) {
  const selectedChatAgent = useAdsWorkspaceStore((s) => s.selectedChatAgent);
  const goBackToChatList = useAdsWorkspaceStore((s) => s.goBackToChatList);
  const subAgentCreatedAt = useAdsWorkspaceStore((s) =>
    selectedChatAgent ? (s.subAgentChats[selectedChatAgent]?.sessionCreatedAt ?? null) : null,
  );
  const { getJobCount, isActive } = useSubAgentStatus();
  const [showSettings, setShowSettings] = useState(false);

  // For Marcus, the prop is authoritative; for sub-agents, the store is.
  const effectiveCreatedAt = selectedChatAgent ? subAgentCreatedAt : sessionCreatedAt;

  useTick(effectiveCreatedAt != null);

  const agentMeta = selectedChatAgent ? getCompanionSubAgent(selectedChatAgent) : null;

  // When a sub-agent isn't selected, resolve the orchestrator persona from
  // an active workflow slug. Unknown or deprecated slugs intentionally do
  // not receive a persona.
  const connectedWorkflowSlug = useAgentChatStore((s) => s.connectedWorkflowSlug);
  const isVideoEditorAgent = connectedWorkflowSlug === "marcus-video-editor";
  const orchestratorAgentId = connectedWorkflowSlug ? resolveAgentId(connectedWorkflowSlug) : null;
  const orchestratorPersona = orchestratorAgentId ? AGENT_PERSONAS[orchestratorAgentId] : null;

  const Icon = agentMeta?.icon;
  const HeaderAvatar = headerVariant === "adsCli" ? agentMeta?.listAvatar : null;
  const name =
    agentMeta?.name ??
    (isVideoEditorAgent ? "Marcus Video Editor" : orchestratorPersona?.humanName) ??
    "Marcus";
  const color = agentMeta?.color ?? "text-primary";
  const headerAccent = agentMeta?.chatBgHex ?? agentMeta?.bgHex ?? "#8b5cf6";
  const tag = selectedChatAgent
    ? (COMPANION_AGENT_TAGS[selectedChatAgent] ?? "Agent")
    : (WORKFLOW_TAG[connectedWorkflowSlug ?? ""] ?? COMPANION_AGENT_TAGS.marcus);

  const jobs = selectedChatAgent ? getJobCount(selectedChatAgent) : 0;
  const active = selectedChatAgent ? isActive(selectedChatAgent) : false;
  const age = formatAge(effectiveCreatedAt);

  // Minimal sub-agent activity badge on the back button — only surfaces
  // OTHER sub-agents (so it's not redundant with the agent icon shown in
  // the current header). Shows the first active agent's icon + a flashing
  // ring; back click still just returns to the chat list.
  const otherActiveSubAgent = ADS_SUB_AGENTS.find(
    (a) => a.subAgentId !== selectedChatAgent && isActive(a.subAgentId),
  );

  // Sub-line priority: streaming → running jobs → "{tag} · {age}"
  // Turn elapsed is kept visible even while a tool is running (when the
  // inline ThinkingBubble steps aside for the ToolCallCard), so the user
  // always has a persistent "it's been N seconds" signal.
  const subLine = isStreaming
    ? turnElapsedMs != null
      ? `Typing… · ${formatHeaderElapsed(turnElapsedMs)}`
      : "Typing…"
    : active
      ? `${tag} · ${jobs} running`
      : age
        ? `${tag} · ${age}`
        : tag;

  const isListening = voiceControls?.isListening ?? false;
  // Hero is shown for Marcus only when avatarHero prop is provided
  const showHero = !selectedChatAgent && avatarHero != null;
  const useAdsCliHeader = headerVariant === "adsCli" && selectedChatAgent && !showHero;
  const [heroAvatarFailed, setHeroAvatarFailed] = useState(false);
  const canUseSimliHero = Boolean(
    showHero &&
    env.nodeEnv !== "test" &&
    env.simliApiKey &&
    avatarHero?.hasValidAvatarId &&
    avatarHero.simliFaceId &&
    avatarHero.voiceId,
  );
  const renderSimliHero = canUseSimliHero && !heroAvatarFailed;

  useEffect(() => {
    setHeroAvatarFailed(false);
  }, [avatarHero?.simliFaceId, avatarHero?.voiceId, canUseSimliHero]);

  const startHeroAvatar = () => {
    if (!renderSimliHero) return;
    avatarHero?.avatarRef.current?.start?.();
  };

  return (
    <div className="flex flex-col shrink-0">
      {/* ── Hero Avatar Section (Marcus only) ── */}
      {showHero && (
        <div className="relative flex flex-col items-center pt-4 pb-2 sm:pt-8 sm:pb-4 px-4 bg-card/40 overflow-hidden">
          {/* Subtle ambient glow */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at center 35%, color-mix(in srgb, var(--primary) 6%, transparent), transparent 65%)",
            }}
          />

          {/* Avatar circle with pulse ring */}
          <div className="relative z-10">
            {(avatarSpeaking || isListening) && (
              <div
                className="absolute -inset-2.5 rounded-full pointer-events-none animate-pulse"
                style={{
                  background: isListening
                    ? "radial-gradient(circle, rgba(239,68,68,0.25), transparent 70%)"
                    : "radial-gradient(circle, color-mix(in srgb, var(--primary) 30%, transparent), transparent 70%)",
                  animationDuration: "700ms",
                }}
              />
            )}
            {renderSimliHero ? (
              <button
                type="button"
                onPointerDown={startHeroAvatar}
                onClick={startHeroAvatar}
                aria-label={`Start ${name} avatar`}
                title={`${name} avatar`}
                className={cn(
                  "w-14 h-14 sm:w-[72px] sm:h-[72px] rounded-full overflow-hidden border-2 bg-black relative shadow-lg p-0",
                  isListening
                    ? "border-red-400/60"
                    : avatarSpeaking
                      ? "border-primary/70"
                      : avatarHero?.avatarConnected
                        ? "border-emerald-400/50"
                        : avatarHero?.avatarConnecting
                          ? "border-amber-400/50"
                          : "border-primary/30",
                )}
              >
                <SimliAvatar
                  ref={avatarHero.avatarRef}
                  faceId={avatarHero.simliFaceId!}
                  voiceId={avatarHero.voiceId!}
                  isMuted={voiceControls?.avatarMuted ?? false}
                  volume={voiceControls?.voiceVolume ?? 1}
                  hideGestureGate
                  posterSrc={MARCUS_HERO_POSTER_SRC}
                  onConnectionAttempt={() => {
                    setHeroAvatarFailed(false);
                    avatarHero.setAvatarConnected(false);
                    avatarHero.setAvatarConnecting(true);
                  }}
                  onConnected={() => {
                    setHeroAvatarFailed(false);
                    avatarHero.setAvatarConnecting(false);
                    avatarHero.setAvatarConnected(true);
                  }}
                  onDisconnected={() => {
                    avatarHero.setAvatarConnecting(false);
                    avatarHero.setAvatarConnected(false);
                    setHeroAvatarFailed(true);
                  }}
                  onStartSpeaking={avatarHero.scheduleAvatarSpeakingStart}
                  onStopSpeaking={avatarHero.scheduleAvatarSpeakingStop}
                  onTextAudioReady={avatarHero.onTextAudioReady}
                />
              </button>
            ) : (
              <div
                className={cn(
                  "w-14 h-14 sm:w-[72px] sm:h-[72px] rounded-full overflow-hidden border-2 bg-black relative shadow-lg",
                  isListening
                    ? "border-red-400/60"
                    : avatarSpeaking
                      ? "border-primary/70"
                      : "border-primary/30",
                )}
              >
                <img
                  src={MARCUS_HERO_POSTER_SRC}
                  alt={name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          {/* Name + status */}
          <div className="mt-3 text-sm font-semibold text-foreground leading-none z-10">{name}</div>
          <div className="mt-1 text-[10px] text-muted-foreground/70 leading-none z-10">
            {subLine}
          </div>
        </div>
      )}

      {/* ── Controls Bar ── */}
      <div
        className={cn(
          useAdsCliHeader
            ? "relative flex items-center gap-3 px-3.5 py-3 bg-card/60 backdrop-blur-sm border-b border-border/30"
            : "flex items-center gap-2 px-3 py-1.5 bg-card/60 backdrop-blur-sm border-b border-border/20",
          showHero && "border-t border-border/10",
        )}
        style={
          useAdsCliHeader
            ? {
                background: `linear-gradient(135deg, ${headerAccent}14 0%, ${headerAccent}06 60%, transparent 100%)`,
              }
            : undefined
        }
      >
        {useAdsCliHeader && (
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{
              background: `linear-gradient(90deg, transparent, ${headerAccent}55, transparent)`,
            }}
            aria-hidden
          />
        )}
        {/* Back */}
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={onBackToList ?? goBackToChatList}
            aria-label="Back"
            title="Back"
            className={cn(
              "flex items-center justify-center rounded-full transition-colors",
              useAdsCliHeader
                ? "h-8 w-8 text-foreground/65 hover:bg-muted/45 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                : "w-6 h-6 hover:bg-muted/40",
            )}
          >
            <ArrowLeft
              className={cn(useAdsCliHeader ? "h-4 w-4" : "w-3.5 h-3.5 text-foreground/70")}
            />
          </button>
          <AnimatePresence>
            {otherActiveSubAgent && (
              <motion.span
                key={`activity-${otherActiveSubAgent.subAgentId}`}
                className="absolute -top-1 -right-1 pointer-events-none"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 420, damping: 22 }}
              >
                <motion.span
                  className="relative flex items-center justify-center w-3.5 h-3.5 rounded-full ring-1 ring-card/80"
                  style={{ backgroundColor: otherActiveSubAgent.bgHex }}
                  animate={{ scale: [1, 1.15, 1] }}
                  transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
                >
                  {React.createElement(otherActiveSubAgent.icon, {
                    className: "w-2 h-2 text-white",
                  })}
                </motion.span>
                <motion.span
                  className="absolute inset-0 rounded-full"
                  style={{
                    boxShadow: `0 0 0 2px color-mix(in srgb, ${otherActiveSubAgent.bgHex} 55%, transparent)`,
                  }}
                  animate={{ opacity: [0.9, 0, 0.9] }}
                  transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
                />
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        {onCreateSession && (
          <button
            type="button"
            onClick={() => void onCreateSession()}
            disabled={sessionControlsDisabled || isStreaming}
            aria-label={`Start new ${name} chat`}
            title="New chat"
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus className="h-3 w-3" />
          </button>
        )}

        {/* Session selector pill (Marcus only) — mirrors the history button with the active title. */}
        {!selectedChatAgent && sessionName && onOpenSessions && (
          <button
            type="button"
            onClick={onOpenSessions}
            aria-label="Open Marcus chat history"
            title="Open chat history"
            className="flex min-w-0 max-w-[120px] shrink items-center gap-0.5 rounded-full bg-muted/40 px-1.5 py-0.5 text-[10px] text-foreground/70 transition-colors hover:bg-muted/60"
          >
            <span className="truncate">{sessionName}</span>
            <ChevronDown className="w-2.5 h-2.5 shrink-0" />
          </button>
        )}

        {/* Agent icon + name — only when hero is NOT shown (sub-agents or no avatarHero) */}
        {!showHero && (
          <>
            <div
              className={cn(
                "relative flex items-center justify-center shrink-0",
                useAdsCliHeader ? "h-[52px] w-[52px] rounded-2xl" : "w-7 h-7 rounded-full",
                active && "ring-1.5 ring-primary/40",
              )}
              style={{
                backgroundColor: agentMeta
                  ? `${useAdsCliHeader ? agentMeta.chatBgHex : agentMeta.bgHex}20`
                  : "var(--primary-5)",
              }}
            >
              {HeaderAvatar ? (
                <HeaderAvatar className="h-[40px] w-[40px] rounded-full" render3d />
              ) : Icon ? (
                <Icon className={cn(useAdsCliHeader ? "h-5 w-5" : "w-3.5 h-3.5", color)} />
              ) : (
                <MessageCircle
                  className={cn(useAdsCliHeader ? "h-5 w-5" : "w-3.5 h-3.5", "text-primary")}
                />
              )}
              {useAdsCliHeader && (
                <span
                  className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-card bg-emerald-500"
                  aria-hidden
                />
              )}

              {/* Blinking notification dot while the sub-agent's session is active */}
              <AnimatePresence>
                {active && (
                  <motion.span
                    className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-primary"
                    style={{
                      boxShadow: "0 0 5px color-mix(in srgb, var(--primary) 70%, transparent)",
                    }}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: [1, 0.35, 1] }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{
                      scale: { duration: 0.25, ease: "easeOut" },
                      opacity: { repeat: Infinity, duration: 1.6, ease: "easeInOut" },
                    }}
                  />
                )}
              </AnimatePresence>
            </div>

            <div className={cn("flex-1 min-w-0", useAdsCliHeader ? "" : "leading-none")}>
              <div
                className={cn(
                  "font-semibold text-foreground truncate",
                  useAdsCliHeader ? "text-sm leading-tight" : "text-xs",
                )}
              >
                {name}
              </div>
              <div
                className={cn(
                  "truncate",
                  useAdsCliHeader
                    ? "mt-0.5 text-[10px] text-muted-foreground uppercase tracking-wide"
                    : "text-[10px] text-muted-foreground/70 mt-0.5",
                )}
              >
                {subLine}
              </div>
            </div>
          </>
        )}

        {/* Spacer pushes voice controls to the right */}
        <div className="flex-1" />

        {headerActions && (
          <div className="relative flex items-center gap-0.5 shrink-0">{headerActions}</div>
        )}

        {/* Context-fill meter (Marcus only) — only renders once we've seen
           a usage report from the server. Sub-agents run in separate sessions
           with their own context so the Marcus meter is not meaningful there. */}
        {!selectedChatAgent && tokenUsage && tokenUsage.contextWindow > 0 && (
          <ContextFillMeter
            contextTokens={tokenUsage.contextTokens}
            contextWindow={tokenUsage.contextWindow}
            isCompacting={isCompacting}
          />
        )}

        {/* Voice controls (Marcus only) */}
        {!selectedChatAgent && voiceControls && (
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              type="button"
              onClick={() => {
                if (voiceControls.isSecureMicContext) voiceControls.toggleListen();
              }}
              disabled={!voiceControls.isSecureMicContext && !voiceControls.isListening}
              aria-label={voiceControls.isListening ? "Stop listening" : "Start voice input"}
              title={voiceControls.isListening ? "Stop listening" : "Start voice input"}
              className={cn(
                "w-6 h-6 flex items-center justify-center rounded-full transition-all",
                voiceControls.isListening
                  ? "bg-red-500 text-white"
                  : !voiceControls.isSecureMicContext
                    ? "text-muted-foreground/30"
                    : "text-foreground/60 hover:bg-muted/40",
              )}
            >
              <Mic className={cn("w-3 h-3", voiceControls.isListening && "animate-pulse")} />
            </button>
            <button
              type="button"
              onClick={() => voiceControls.setAvatarMuted(!voiceControls.avatarMuted)}
              aria-label={voiceControls.avatarMuted ? "Unmute avatar" : "Mute avatar"}
              title={voiceControls.avatarMuted ? "Unmute avatar" : "Mute avatar"}
              className="w-6 h-6 flex items-center justify-center rounded-full text-foreground/60 hover:bg-muted/40 transition-colors"
            >
              {voiceControls.avatarMuted ? (
                <VolumeX className="w-3 h-3" />
              ) : (
                <Volume2 className="w-3 h-3" />
              )}
            </button>

            {/* Voice / Chat toggle */}
            <div className="flex items-center gap-0.5 bg-muted/50 rounded-md p-0.5 ml-0.5">
              <button
                type="button"
                onClick={() => voiceControls.setCompanionMode("voice")}
                aria-label="Switch to voice mode"
                title="Voice mode"
                className={cn(
                  "px-1 py-0.5 rounded transition-all",
                  !voiceControls.isChatMode
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <AudioLines className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={() => voiceControls.setCompanionMode("chat")}
                aria-label="Switch to chat mode"
                title="Chat mode"
                className={cn(
                  "px-1 py-0.5 rounded transition-all",
                  voiceControls.isChatMode
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <MessageCircle className="w-3 h-3" />
              </button>
            </div>

            {/* Settings — voice volume slider */}
            <div className="relative ml-0.5">
              <button
                type="button"
                onClick={() => setShowSettings((s) => !s)}
                className={cn(
                  "w-6 h-6 flex items-center justify-center rounded-full transition-all",
                  showSettings
                    ? "bg-primary/15 text-primary"
                    : "text-foreground/60 hover:bg-muted/40",
                )}
                aria-label="Settings"
                title="Voice settings"
              >
                <Settings className="w-3 h-3" />
              </button>
              {showSettings && (
                <div className="absolute right-0 top-full mt-1.5 w-[min(13rem,calc(100vw-2rem))] max-w-screen-safe bg-card/95 backdrop-blur-[16px] rounded-xl shadow-lg p-3.5 z-50 animate-in fade-in zoom-in-95 duration-200 border border-border/60">
                  <label className="text-[10px] font-semibold text-foreground mb-2 flex items-center justify-between uppercase tracking-wider">
                    <span>Voice Volume</span>
                    <span className="text-muted-foreground font-normal normal-case">
                      {Math.round(voiceControls.voiceVolume * 100)}%
                    </span>
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={voiceControls.voiceVolume}
                    aria-label="Voice volume"
                    onChange={(e) => voiceControls.setVoiceVolume(Number(e.target.value))}
                    className="w-full accent-primary h-1"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Session assets strip (Banan / Vid / Vidzz) ── */}
      {selectedChatAgent && supportsSubAgentAssetStrip(selectedChatAgent) && (
        <SubAgentSessionAssetsStrip agentId={selectedChatAgent} />
      )}
    </div>
  );
}
